import { trackEvent } from '@/utils/gtags';

/**
 * ### SolutionId
 *
 * Stable `solution_id` dimension every solution landing-page event carries.
 * Reports and dashboards filter on these literals, so a member is never
 * renamed; a page that starts reporting adds its own member here.
 */
export type SolutionId = 'listen_ai_industrial' | 'listen_ai_safety';

/**
 * ### SOLUTION_CONTENT_TYPES
 *
 * Every `content_type` a solution page may report on `select_content`. Markup
 * opts a control into click tracking by setting `data-analytics-content-type`
 * to one of these; any other value is ignored rather than reported.
 */
export const SOLUTION_CONTENT_TYPES = [
  'case_study',
  'demo_video',
  'poc_cta',
  'cta',
  'case_card',
  'ask_ai',
  'faq',
] as const;

/**
 * ### SolutionContentType
 *
 * One member of {@link SOLUTION_CONTENT_TYPES}.
 */
export type SolutionContentType = (typeof SOLUTION_CONTENT_TYPES)[number];

/**
 * ### isSolutionContentType
 *
 * Narrows a value read from markup to a reportable content type.
 *
 * @param value Raw attribute value, usually `dataset.analyticsContentType`
 * @returns Whether the value is one of {@link SOLUTION_CONTENT_TYPES}
 */
export function isSolutionContentType(
  value: unknown,
): value is SolutionContentType {
  return (
    typeof value === 'string' &&
    (SOLUTION_CONTENT_TYPES as ReadonlyArray<string>).includes(value)
  );
}

/**
 * ### SolutionSectionView
 *
 * A landing-page section entering the viewport for the first time.
 */
export type SolutionSectionView = {
  /**
   * ### language
   *
   * Page language as routed, reported as `page_language`.
   */
  language: string;

  /**
   * ### sectionId
   *
   * Value of the section's `data-analytics-section` attribute.
   */
  sectionId: string;

  /**
   * ### sectionPosition
   *
   * One-based order of the section among the page's tracked sections.
   */
  sectionPosition: number;
};

/**
 * ### SolutionContentSelection
 *
 * A reader choosing one piece of content inside a section.
 */
export type SolutionContentSelection = {
  /**
   * ### contentId
   *
   * Stable identifier of the chosen item, reported as `item_id`.
   */
  contentId: string;

  /**
   * ### contentType
   *
   * Kind of content chosen, reported as `content_type`.
   */
  contentType: SolutionContentType;

  /**
   * ### sectionId
   *
   * Section the item belongs to, reported as `section_id`.
   */
  sectionId: string;
};

/**
 * ### SolutionAudioPlay
 *
 * The first successful playback of one audio sample.
 */
export type SolutionAudioPlay = {
  /**
   * ### sampleId
   *
   * Stable identifier of the sample, reported as `item_id`.
   */
  sampleId: string;

  /**
   * ### sectionId
   *
   * Section that hosts the sample player, reported as `section_id`.
   */
  sectionId: string;
};

/**
 * ### trackSolutionSectionView
 *
 * Reports `view_solution_section` for a section that scrolled into view.
 * Deduplication per section is the caller's job; this reports every call.
 *
 * @param solutionId Solution whose page the section belongs to
 * @param view Section identity and its order within the page
 */
export function trackSolutionSectionView(
  solutionId: SolutionId,
  { language, sectionId, sectionPosition }: SolutionSectionView,
): void {
  trackEvent('view_solution_section', {
    solution_id: solutionId,
    section_id: sectionId,
    section_position: sectionPosition,
    page_language: language,
  });
}

/**
 * ### trackSolutionContentSelection
 *
 * Reports `select_content` for content a reader chose. A lead conversion is a
 * separate event owned by the inquiry flow; a call-to-action click here only
 * records the intent to leave the page.
 *
 * @param solutionId Solution whose page the content belongs to
 * @param selection Chosen item, its kind, and its section
 */
export function trackSolutionContentSelection(
  solutionId: SolutionId,
  { contentId, contentType, sectionId }: SolutionContentSelection,
): void {
  trackEvent('select_content', {
    content_type: contentType,
    item_id: contentId,
    solution_id: solutionId,
    section_id: sectionId,
  });
}

/**
 * ### trackSolutionAudioPlay
 *
 * Reports `play_audio_sample` for a sample that started playing. The player
 * decides what counts as the first playback; this reports every call.
 *
 * @param solutionId Solution whose page hosts the sample
 * @param play Sample identity and the section hosting its player
 */
export function trackSolutionAudioPlay(
  solutionId: SolutionId,
  { sampleId, sectionId }: SolutionAudioPlay,
): void {
  trackEvent('play_audio_sample', {
    content_type: 'audio_sample',
    item_id: sampleId,
    solution_id: solutionId,
    section_id: sectionId,
  });
}
